import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Store, ArrowLeft, Eye, EyeOff, Check, X, CreditCard, Shield, Sparkles, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { platformApi } from '@/utils/platformApi';
import { getPasswordComplexityErrors, PASSWORD_HINT } from '@/utils/passwordValidation';

export default function PlatformSignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1); // 1: Store Details, 2: Plan Selection
  const [plans, setPlans] = useState([]);
  const [plansError, setPlansError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    shopName: '',
    slug: '',
    adminName: '',
    adminEmail: '',
    adminPhone: '',
    adminPassword: ''
  });

  // Check if returning from canceled checkout
  useEffect(() => {
    if (searchParams.get('canceled') === 'true') {
      toast.error('Payment setup was canceled. Please try again to complete your registration.');
    }
  }, [searchParams]);

  const loadPlans = useCallback(async () => {
    try {
      setPlansError(null);
      const response = await platformApi.get('/subscriptions/plans');
      // For now, only the Fulfillment tier is offered to new signups.
      // Self-Managed plans are hidden until business is ready to launch them.
      const fetched = (response.data.plans || []).filter(
        (p) => p.subscription_type === 'fulfillment'
      );
      setPlans(fetched);

      if (fetched.length > 0) {
        // Honor `?plan=fulfillment` / `?plan=self_managed` query param coming
        // from the landing page pricing CTAs. Match against `subscription_type`
        // and prefer the monthly cadence so the user sees the entry-level price.
        const requestedType = searchParams.get('plan');
        let preselected = null;
        if (requestedType) {
          preselected =
            fetched.find(
              (p) => p.subscription_type === requestedType && p.interval === 'month'
            ) || fetched.find((p) => p.subscription_type === requestedType);
        }

        if (!preselected) {
          // Default to the cheapest monthly plan, falling back to the first plan.
          // Never preselect a stale id — it must come from what the API returns.
          preselected = fetched.find((p) => p.interval === 'month') || fetched[0];
        }
        setSelectedPlan(preselected.id);
      }
    } catch (error) {
      console.error('Failed to load plans:', error);
      setPlansError(error.message || 'Could not load subscription plans');
    }
  }, [searchParams]);

  // Load plans
  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // Auto-generate slug from shop name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50);
  };

  const handleShopNameChange = (value) => {
    setFormData(prev => ({
      ...prev,
      shopName: value,
      slug: prev.slug || generateSlug(value)
    }));
  };

  // Validate slug format
  const isValidSlug = (slug) => {
    return /^[a-z][a-z0-9-]{2,49}$/.test(slug);
  };

  const handleContinue = (e) => {
    e.preventDefault();
    
    // Validate step 1 fields
    if (!formData.shopName || !formData.slug || !formData.adminEmail || !formData.adminPhone || !formData.adminPassword) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!isValidSlug(formData.slug)) {
      toast.error('Store URL must be 3-50 characters, start with a letter, and contain only lowercase letters, numbers, and hyphens');
      return;
    }

    const pwErrors = getPasswordComplexityErrors(formData.adminPassword);
    if (pwErrors.length > 0) {
      toast.error(`Password must contain ${pwErrors.join(', ')}.`);
      return;
    }

    // Move to plan selection
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      const response = await platformApi.post('/platform/tenants/signup', {
        store_name: formData.shopName.trim(),
        store_slug: formData.slug.toLowerCase().trim(),
        admin_name: formData.adminName.trim() || `Admin (${formData.shopName})`,
        admin_email: formData.adminEmail.trim(),
        admin_phone: formData.adminPhone.trim(),
        admin_password: formData.adminPassword,
        plan_id: selectedPlan,
        origin_url: window.location.origin
      });

      const { checkout_url } = response.data;
      
      if (checkout_url) {
        toast.success('Redirecting to payment setup...');
        // Redirect to Stripe Checkout
        window.location.href = checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to create store. Please try again.';
      
      if (typeof detail === 'string') {
        if (detail.includes('already exists')) {
          errorMessage = 'This store URL is already taken. Please choose another.';
          setStep(1);
        } else if (detail.includes('reserved')) {
          errorMessage = 'This store URL is reserved. Please choose another.';
          setStep(1);
        } else if (detail.includes('already registered')) {
          errorMessage = 'This email is already registered. Please use a different email or sign in.';
          setStep(1);
        } else {
          errorMessage = detail;
        }
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(err => err.msg || err.message).join('. ');
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const slugValid = formData.slug ? isValidSlug(formData.slug) : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F3EF] to-white flex flex-col">
      {/* Header */}
      <header className="border-b border-[#E8E4DC] bg-white/80 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-10 w-10 bg-[#4A6741] rounded-lg flex items-center justify-center">
              <Store className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-[#2C3E2D]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Golfshop.fit
            </span>
          </Link>
          <Link to="/" className="text-[#5A6B5C] hover:text-[#4A6741] flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Signup Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-2xl">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#4A6741]' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-[#4A6741] text-white' : 'bg-gray-200'}`}>
                  {step > 1 ? <Check className="h-4 w-4" /> : '1'}
                </div>
                <span className="hidden sm:inline font-medium">Store Details</span>
              </div>
              <div className="w-12 h-0.5 bg-gray-200" />
              <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#4A6741]' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-[#4A6741] text-white' : 'bg-gray-200'}`}>
                  2
                </div>
                <span className="hidden sm:inline font-medium">Choose Plan</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-[#E8E4DC] p-8 shadow-sm">
            {step === 1 ? (
              <>
                <h1 
                  className="text-2xl font-bold text-[#2C3E2D] mb-2 text-center"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                  data-testid="signup-title"
                >
                  Create Your Shop
                </h1>
                <p className="text-[#5A6B5C] text-center mb-8">
                  Set up your golf equipment store in minutes
                </p>

                <form onSubmit={handleContinue} className="space-y-5">
                  {/* Shop Details */}
                  <div className="border-b border-[#E8E4DC] pb-5">
                    <h3 className="text-sm font-bold text-[#5A6B5C] uppercase tracking-wider mb-4">
                      Shop Details
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="shopName" className="text-[#2C3E2D]">Shop Name *</Label>
                        <Input
                          id="shopName"
                          type="text"
                          placeholder="e.g., Bob's Golf Shop"
                          value={formData.shopName}
                          onChange={(e) => handleShopNameChange(e.target.value)}
                          className="mt-1 border-[#E8E4DC]"
                          data-testid="signup-shop-name"
                        />
                      </div>

                      <div>
                        <Label htmlFor="slug" className="text-[#2C3E2D]">Store URL *</Label>
                        <div className="flex items-center mt-1 relative">
                          <span className="text-[#5A6B5C] text-sm bg-gray-100 px-3 py-2.5 rounded-l-md border border-r-0 border-[#E8E4DC]">
                            /
                          </span>
                          <Input
                            id="slug"
                            type="text"
                            placeholder="your-store"
                            value={formData.slug}
                            onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase() }))}
                            className={`rounded-l-none border-[#E8E4DC] pr-10 ${
                              slugValid === false ? 'border-red-500 focus:ring-red-500' : 
                              slugValid === true ? 'border-green-500' : ''
                            }`}
                            data-testid="signup-slug"
                          />
                          {slugValid !== null && (
                            <div className="absolute right-3">
                              {slugValid ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <X className="h-4 w-4 text-red-500" />
                              )}
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-[#5A6B5C] mt-1">
                          This will be your store's web address (e.g., /bobs-golf)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Admin Account */}
                  <div>
                    <h3 className="text-sm font-bold text-[#5A6B5C] uppercase tracking-wider mb-4">
                      Admin Account
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="adminName" className="text-[#2C3E2D]">Your Name (optional)</Label>
                        <Input
                          id="adminName"
                          type="text"
                          placeholder="e.g., John Smith"
                          value={formData.adminName}
                          onChange={(e) => setFormData(prev => ({ ...prev, adminName: e.target.value }))}
                          className="mt-1 border-[#E8E4DC]"
                          data-testid="signup-admin-name"
                        />
                      </div>

                      <div>
                        <Label htmlFor="adminEmail" className="text-[#2C3E2D]">Email *</Label>
                        <Input
                          id="adminEmail"
                          type="email"
                          placeholder="you@example.com"
                          value={formData.adminEmail}
                          onChange={(e) => setFormData(prev => ({ ...prev, adminEmail: e.target.value }))}
                          className="mt-1 border-[#E8E4DC]"
                          data-testid="signup-admin-email"
                        />
                      </div>

                      <div>
                        <Label htmlFor="adminPhone" className="text-[#2C3E2D]">Phone Number *</Label>
                        <Input
                          id="adminPhone"
                          type="tel"
                          placeholder="e.g., (555) 123-4567"
                          value={formData.adminPhone}
                          onChange={(e) => setFormData(prev => ({ ...prev, adminPhone: e.target.value }))}
                          className="mt-1 border-[#E8E4DC]"
                          data-testid="signup-admin-phone"
                        />
                        <p className="text-xs text-[#5A6B5C] mt-1">
                          Shown to customers who need to call for pricing or support
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="adminPassword" className="text-[#2C3E2D]">Password *</Label>
                        <div className="relative mt-1">
                          <Input
                            id="adminPassword"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Min 8 chars, upper/lower/number/symbol"
                            value={formData.adminPassword}
                            onChange={(e) => setFormData(prev => ({ ...prev, adminPassword: e.target.value }))}
                            className="border-[#E8E4DC] pr-10"
                            data-testid="signup-admin-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A6B5C] hover:text-[#4A6741]"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <p className="text-xs text-[#5A6B5C] mt-1">{PASSWORD_HINT}</p>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#4A6741] hover:bg-[#3d5636] text-white py-5"
                    data-testid="signup-continue"
                  >
                    Continue to Plan Selection
                  </Button>
                </form>
              </>
            ) : (
              <>
                <button 
                  onClick={() => setStep(1)} 
                  className="text-[#5A6B5C] hover:text-[#4A6741] flex items-center gap-1 mb-4"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to details
                </button>
                
                <h1 
                  className="text-2xl font-bold text-[#2C3E2D] mb-2 text-center"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                >
                  Choose Your Plan
                </h1>
                <p className="text-[#5A6B5C] text-center mb-2">
                  Start with a <span className="font-bold text-[#4A6741]">7-day free trial</span>
                </p>
                <p className="text-sm text-[#5A6B5C] text-center mb-8">
                  Cancel anytime. Your card will only be charged after the trial ends.
                </p>

                {/* Plan Cards / Error / Loading state */}
                {plansError ? (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-6 mb-8 text-center" data-testid="plans-error">
                    <p className="font-semibold text-amber-900 mb-1">Couldn&apos;t load plans</p>
                    <p className="text-sm text-amber-800 mb-4">{plansError}</p>
                    <button
                      type="button"
                      onClick={loadPlans}
                      className="px-4 py-2 bg-[#2C3E2D] hover:bg-[#1a2a1b] text-white text-sm font-semibold rounded-md"
                      data-testid="plans-retry-btn"
                    >
                      Try Again
                    </button>
                  </div>
                ) : plans.length === 0 ? (
                  <div className="grid md:grid-cols-2 gap-4 mb-8" data-testid="plans-loading">
                    {[0, 1].map((i) => (
                      <div key={i} className="border-2 border-[#E8E4DC] rounded-xl p-6 animate-pulse">
                        <div className="h-4 w-24 bg-gray-200 rounded mb-3" />
                        <div className="h-8 w-32 bg-gray-200 rounded mb-4" />
                        <div className="h-3 w-full bg-gray-100 rounded mb-2" />
                        <div className="h-3 w-5/6 bg-gray-100 rounded mb-2" />
                        <div className="h-3 w-2/3 bg-gray-100 rounded" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4 mb-8">
                    {plans.map((plan) => (
                      <div
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan.id)}
                        className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${
                          selectedPlan === plan.id
                            ? 'border-[#4A6741] bg-[#4A6741]/5'
                            : 'border-[#E8E4DC] hover:border-[#4A6741]/50'
                        }`}
                        data-testid={`plan-${plan.id}`}
                      >
                        {plan.badge && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#4A6741] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                            <Sparkles className="h-3 w-3" />
                            {plan.badge}
                          </div>
                        )}
                        
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-bold text-[#2C3E2D]">{plan.name}</h3>
                            <p className="text-2xl font-bold text-[#4A6741]">
                              ${plan.price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                              <span className="text-sm font-normal text-[#5A6B5C]">/{plan.interval}</span>
                            </p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedPlan === plan.id ? 'border-[#4A6741] bg-[#4A6741]' : 'border-gray-300'
                          }`}>
                            {selectedPlan === plan.id && <Check className="h-3 w-3 text-white" />}
                          </div>
                        </div>
                        
                        <ul className="space-y-2 text-sm">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-center gap-2 text-[#5A6B5C]">
                              <Check className="h-4 w-4 text-[#4A6741]" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {/* Trial Info */}
                <div className="bg-[#F5F3EF] rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-[#4A6741] mt-0.5" />
                    <div>
                      <p className="font-medium text-[#2C3E2D]">Risk-free trial</p>
                      <p className="text-sm text-[#5A6B5C]">
                        Try all features free for 7 days. We&apos;ll send you a reminder 2 days before your trial ends.
                        Cancel anytime in your dashboard - no questions asked.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Trial-ends-on-first-sale disclaimer */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6" data-testid="trial-ends-on-sale-disclaimer">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-900">Heads up: your trial ends on your first sale</p>
                      <p className="text-sm text-amber-800 mt-1">
                        The moment a customer places a paid order on your storefront, your free trial ends and your subscription is charged immediately. You keep full access — only the trial countdown stops.
                      </p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <Button
                    type="submit"
                    disabled={loading || !selectedPlan}
                    className="w-full bg-[#4A6741] hover:bg-[#3d5636] text-white py-5 flex items-center justify-center gap-2"
                    data-testid="signup-submit"
                  >
                    <CreditCard className="h-5 w-5" />
                    {loading ? 'Setting up...' : 'Start Free Trial'}
                  </Button>
                  
                  {selectedPlan && (() => {
                    const sel = plans.find((p) => p.id === selectedPlan);
                    if (!sel) return null;
                    const priceStr = `$${Number(sel.price).toLocaleString('en-US')}`;
                    return (
                      <p className="text-xs text-center text-[#5A6B5C] mt-3">
                        By clicking &quot;Start Free Trial&quot;, you agree to our Terms of Service and authorize
                        us to charge your card {priceStr}/{sel.interval} after your 7-day trial ends unless you cancel.
                      </p>
                    );
                  })()}
                </form>
              </>
            )}

            <div className="mt-6 text-center">
              <p className="text-[#5A6B5C] text-sm">
                Already have a store?{' '}
                <Link to="/login" className="text-[#4A6741] font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
